<template>
	<div id="root">
		<router-view/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import store from './store/store';
import fundModuleList from '_shared_/utils/fund-detail-modules';
import { throttle } from 'lodash';

export default {
	name: 'Root',
	data() {
		return {
			hasFiredInitialDisclosureEvent: false,
			hasFiredInitialIBallEvent: false
		};
	},
	computed: {
		...mapGetters([
			'getAudience',
			'disclosureCollection',
			'getFundInfoData',
			'getReturnsPageFundAbbreviations',
			'getReturnsPageIndexIds',
			'IBallCollection',
			'selectedChartTab',
			'showDetailModal'
		])
	},
	beforeMount() {
		const that = this;

		F2.Events.on('disclosures_have_updated', data => {
			that.$store.commit('updateDisclosureSuperscripts', data);
		});

		F2.Events.on('iball_descriptions_have_updated', data => {
			that.$store.commit('updateIBallDescriptions', data);
			F2.Events.emit('load_iball_descriptions');
		});

		//when a disclosure component is added to the page from within the beforeMount lifecycle method, this even is fired
		this.$EventBus.$on(
			'additional-disclosure-mounted',
			haveFiltersChanged => {
				if (that.hasFiredInitialDisclosureEvent) {
					that.updateDisclosure(haveFiltersChanged);
				}
			}
		);

		this.$EventBus.$on('additional-iball-mounted', () => {
			if (that.hasFiredInitialIBallEvent) {
				this.updateIBall();
			}
		});

		this.$nextTick(() => {
			window.addEventListener('resize', () => {
				that.$store.commit('setBreakpoint', window.innerWidth);
			});
		});
	},
	beforeRouteEnter(to, from, next) {
		switch (to.name) {
			case 'Details':
			case 'Quarterly-Holdings':
				next(vm => vm.getDetailsAndQuarterlyData());
				break;
			case 'Returns':
				next(vm => vm.getReturnsData());
				break;
			default:
				next();
				break;
		}
	},
	mounted() {
		this.updatePopperBoundarySize();
		window.onbeforeunload = () => {
			window.scrollTo(0, 0);
		};
		window.addEventListener(
			'resize',
			throttle(this.updatePopperBoundarySize, 100, {})
		);
		window.addEventListener(
			'scroll',
			throttle(this.updatePopperBoundarySize, 100, {})
		);
	},
	methods: {
		// Parameters are added in src/store/modules/actions.js
		// Add the data for the action here, and add the string value with data value in actions.js
		async getDetailsAndQuarterlyData() {
			const nocache = this.$route.query['..nocache..'];
			let fundData = await store.dispatch('getAndCommit', {
				endpoint: 'fund-universe',
				commitId: 'SET_FUND_ATTRIBUTES',
				audience: this.getAudience,
				fundClassSymbol: store.state.appConfig.context.shareClassSymbol,
				nocache
			});
			fundData = fundData?.data?.data?.fund ?? {};
			let apiPathsKey = '';
			if (this.$route.name === 'Quarterly-Holdings') {
				apiPathsKey = 'quarterlyHoldings';
			} else if (this.$route.name === 'Returns') {
				apiPathsKey = 'returnsPage';
			} else {
				apiPathsKey = fundData.Objective || '';
			}
			const paths = fundModuleList.getApiPaths(apiPathsKey);

			let fundSymbol = '';
			const audience = this.getAudience;
			const fundNumber = fundData.FundNumber || '';
			const fundClassNumber = fundData.FundClassNumber || '';
			let shareClass = fundData.ShareClass || '';
			let sortBy = null;
			let sortDirection = null;
			let startDate = '';
			let endDate = '';
			const period = '';
			const ticker = this.$store.state.appConfig.context.shareClassSymbol || '';
			const year = `${new Date().getFullYear()}`;

			if (apiPathsKey === 'quarterlyHoldings') {
				sortBy = 'pctNetAssets';
				sortDirection = 'desc';
			}

			const primaryAPICallList = [];
			for (const primaryCall of paths.primaryCalls) {
				const indexId =
					primaryCall.sendIndexId && fundData.CGIndexID
						? fundData.CGIndexID
						: '';
				const assetClass =
					primaryCall.sendAssetClass && fundData.AssetClass
						? fundData.AssetClass
						: '';
				//To fix data issue with underlying funds, defaults to share class A in QID if nothing is passed in
				if (primaryCall.value.includes('underlyingFunds')) {
					shareClass = '';
				} else {
					shareClass = fundData.ShareClass || '';
				}

				if (primaryCall.value.includes('returns-module')) {
					fundSymbol = fundData.FundSymbol || '';
				} else {
					fundSymbol = '';
				}

				if (primaryCall.value.includes('historicalprices')) {
					endDate = `${new Date().getFullYear()}`;
					startDate = `${new Date().getFullYear() - 1}`;
				} else {
					startDate = '';
					endDate = '';
				}

				const apiCall = store.dispatch('getAndCommit', {
					endpoint: primaryCall.value,
					commitId: primaryCall.key,
					mutationNeedsAudience: primaryCall.mutationNeedsAudience,
					noSymbol: primaryCall.noSymbol,
					fundClassNumber,
					fundNumber,
					shareClass,
					audience,
					stagingFlag: this.$store.state.stagingFlag,
					fundSymbol,
					indexId,
					assetClass,
					sortBy,
					sortDirection,
					startDate,
					endDate,
					period,
					year,
					ticker,
					nocache
				});
				primaryAPICallList.push(apiCall);
			}
			await Promise.all(primaryAPICallList);

			const inceptionDate =
				this.$store.state?.fundFacts?.fundFacts?.InceptionDate?.data
				?? '';

			const secondaryAPICallList = [];
			for (const secondaryCall of paths.secondaryCalls) {
				const indexId = fundData.CGIndexID;
				const apiCall = store.dispatch('getAndCommit', {
					endpoint: secondaryCall.value,
					commitId: secondaryCall.key,
					fundNumber,
					shareClass,
					stagingFlag: this.$store.state.stagingFlag,
					indexId,
					inceptionDate,
					nocache
				});
				secondaryAPICallList.push(apiCall);
			}
			await Promise.all(secondaryAPICallList);

			this.updateDisclosure();
			this.updateIBall();
		},
		async getReturnsData() {
			const nocache = this.$route.query['..nocache..'] || '';
			await store.dispatch('getAndCommit', {
				endpoint: 'fund-universe',
				commitId: 'SET_FUND_ATTRIBUTES',
				audience: this.getAudience,
				fundClassSymbol: store.state.appConfig.context.shareClassSymbol,
				nocache
			});
			await store.dispatch('getAndCommit', {
				endpoint: 'returns-all',
				commitId: 'SET_RETURNS_PAGE',
				dataSet: this.$store.state.stagingFlag,
				audience: 'AFIS Default',
				nocache
			});
			await store.dispatch('getAndCommit', {
				endpoint: 'returns-filters',
				commitId: 'SET_RETURNS_FILTERS',
				stagingFlag: this.$store.state.stagingFlag,
				audience: this.getAudience,
				mutationNeedsAudience: true,
				nocache
			});
			this.updateDisclosure();
			this.updateIBall();
		},
		updateIBall() {
			const that = this;
			that.$nextTick(() => {
				const IBallElements = [].slice.call(
					document.querySelectorAll('*[iball]')
				);
				const IBallCollection = [
					...new Set(
						IBallElements.map(el => el.getAttribute('iball'))
					)
				];
				if (
					!this.isSetEqual(
						new Set(this.IBallCollection),
						new Set(IBallCollection)
					)
				) {
					this.$store.commit(
						'updateIBallCollection',
						IBallCollection
					);
					/*eslint-disable no-console*/
					console.log(
						'IBall Collection: ',
						JSON.stringify(IBallCollection)
					);
					F2.Events.emit(
						'update_iball_description_list',
						JSON.stringify(IBallCollection)
					);
				}
				this.hasFiredInitialIBallEvent = true;
			});
		},
		updateDisclosure(haveFiltersChanged) {
			const that = this;
			that.$nextTick(() => {
				//this query string is used to only collect the disclosures for a specific view
				//either the details page, or the returns view on the modal if its open, or the volatility view of the modal
				// as those views have their own order but when the modal is open the detail disclosures are still mounted so the distinction is needed

				let queryString = '';
				if (that.$route.name === 'Returns') {
					queryString = '.returns-page.disclosure-superscript';
				} else if (that.$route.name === 'Quarterly-Holdings') {
					queryString = '.quarterly-holdings-page.disclosure-superscript';
					//it's on the detail page and no modal is open
				} else if (!that.showDetailModal) {
					queryString = '.detail-page.disclosure-superscript';
					//else its on the details page and the modal is open, so check which tab
				} else if (that.selectedChartTab === 'annual') {
					queryString = '.returns-modal.disclosure-superscript';
				} else if (that.selectedChartTab === 'growth') {
					queryString = '.growth-modal.disclosure-superscript';
				} else queryString = '.volatility-modal.disclosure-superscript';
				//collected the elements
				const superscriptElements = [].slice.call(
					document.querySelectorAll(queryString)
				);
				//only save unique anchors (any duplicate any will have match statistic numbers as long as one anchor is accounted for)
				const recentlyMountedUniqueAnchors = new Set();
				const disclosureStatisticsCollection = [];
				let order = 1;
				//loop over the elements and construct an object formatted to CG's needs
				superscriptElements.forEach((el, index) => {
					const obj = {};
					const anchor = el.getAttribute(
						'disclosure-superscript-anchor'
					);
					if (!recentlyMountedUniqueAnchors.has(anchor)) {
						recentlyMountedUniqueAnchors.add(anchor);
						obj[anchor] = {
							order,
							statistics: el.getAttribute('statistics').split(',')
						};
						order += 1;
						disclosureStatisticsCollection.push(obj);
					}
				});

				//because this original event is fired for each disclosure that gets mounted,this verifies that a new anchor/new disclosure has been mounted, and only calls the event for CG
				//when a group of new disclosures has been rendered (rather than calling the CG event for disclosures for every single disclosure thats get mounted)
				const preExistingAnchors = new Set(
					that.disclosureCollection.statistics.reduce((acc, item) => {
						acc.push(Object.keys(item)[0]);
						return acc;
					}, [])
				);

				//collect the strategy symbols and index ID's to be sent with the commit object
				//returns page has many, details has only one

				const fundSymbol =
					that?.getFundInfoData?.fundAbbreviation?.split('-')[0] ??
					'';

				const preExistingProducts = new Set(
					that.disclosureCollection.products
				);

				const products = new Set();
				if (this.$route.name === 'Returns') {
					const fundAbbreviations = this.getReturnsPageFundAbbreviations;
					fundAbbreviations?.forEach(fundAbbreviation => {
						products.add(fundAbbreviation);
					});
				} else {
					fundSymbol && products.add(fundSymbol);
				}

				const primaryIndexId = that?.getFundInfoData?.cgIndexID ?? null;
				//Use returns page index ids if on returns page. Use Primary Index ID on detail page if it exists, use nothing on QH page
				const indexIds = [];
				if (this.$route.name === 'Returns') {
					const returnsPageIndexIds = this.getReturnsPageIndexIds;
					returnsPageIndexIds?.forEach(indexId => {
						indexIds.push(indexId);
					});
				} else if (this.$route.name !== 'Quarterly-Holdings') {
					primaryIndexId && indexIds.push(primaryIndexId);
				}

				//verify if new disclosures have mounted (via checking is an additional/new statistics is applied on the page)
				// as well as if there has been a new strategy added on the page
				const newProductsHaveRendered = !this.isSetEqual(
					products,
					preExistingProducts
				);
				const newAnchorsHaveRendered = !this.isSetEqual(
					recentlyMountedUniqueAnchors,
					preExistingAnchors
				);
				const disclosureCollection = {
					products: Array.from(products),
					statistics: disclosureStatisticsCollection,
					indexIds
				};
				if (
					newAnchorsHaveRendered ||
					newProductsHaveRendered ||
					haveFiltersChanged
				) {
					that.$store.commit(
						'updateDisclosureCollection',
						disclosureCollection
					);
				}
				that.hasFiredInitialDisclosureEvent = true;
			});
		},
		updatePopperBoundarySize(event) {
			try {
				const headerDim = document
					.getElementById('top-header')
					.getBoundingClientRect();
				const windowHeight = window.innerHeight;
				const headerBottom =
					headerDim.bottom > 0 ? headerDim.bottom : 0;
				document.getElementById(
					'popper-boundary'
				).style.cssText = `height: ${windowHeight -
					headerBottom}px; top: ${headerBottom}px; width: ${
					headerDim.width
				}px;`;
			} catch (err) {
				//
			}
		},
		isSetEqual(as, bs) {
			if (as.size !== bs.size) return false;
			for (const a of as) if (!bs.has(a)) return false;
			return true;
		}
	}
};
</script>
